// assets/cart-recovery.js
//
// CS-316: broken-cart recovery — single source of truth.
//
// Mechanism: each add-to-cart from the imprint flow creates product line(s)
// AND a matching "Associated Fee" line, tied together by the hidden
// `_groupID` property. A failed/partial add (e.g. fee added but products
// failed, or vice versa) can leave one without the other. The custom cart
// page groups lines by `_groupID`, so such a group renders as nothing —
// an "empty/broken cart" even though Shopify's own cart icon still shows
// the item count.
//
// This file is plain vanilla JS (no Vue/Pinia/axios dependency) so it can be
// loaded cheaply on ANY page — wired into:
//   - snippets/product--scripts.liquid (product page, loaded alongside the
//     imprint-app Vue bundle, which calls window.__cartRecovery.recoverCart()
//     instead of duplicating this logic)
//   - sections/cart-template.liquid (cart page, runs standalone before
//     cart.storage.js reads localStorage)
//
// Exposed globally as window.__cartRecovery = { recoverCart, findOrphanCartLines, removeCartLines, clearShopifyCart }
(function () {
    /**
     * Identify "orphan" lines left by a partial add-to-cart.
     * Fee lines are identified by SKU prefix "FEE_" (see
     * web/app/Services/TemporaryProductAllocator.php::addFeeVariant() in the
     * imprint-app backend — $feeSku = 'FEE_' . $colorPrId . '_' . $timestamp . '_' . $randSuffix).
     *
     * @param {Object} cart - Shopify cart object (from /cart.js or cart/update.js)
     * @returns {Array<string>} line item keys to remove
     */
    function findOrphanCartLines(cart) {
        var items = (cart && cart.items) || [];
        var groups = {};

        console.log('[cart-remove-check] findOrphanCartLines() called with', items.length, 'cart items');
        items.forEach(function (line) {
            console.log('[cart-remove-check] line:', {
                title: line.title,
                sku: line.sku,
                key: line.key,
                groupID: line.properties && line.properties._groupID,
                isFeeBySku: typeof line.sku === 'string' && line.sku.indexOf('FEE_') === 0
            });
        });

        items.forEach(function (line) {
            var gid = line.properties && line.properties._groupID;
            if (!gid) return; // not one of our grouped lines — leave it alone
            var isFee = typeof line.sku === 'string' && line.sku.indexOf('FEE_') === 0;
            if (!groups[gid]) groups[gid] = { fees: [], products: [], incomplete: false };
            // Shopify storefront replication lag: a just-created variant can
            // briefly come back with sku=null (title may or may not also be
            // null — they don't always resolve together). sku is the field
            // the fee/product classification actually depends on (isFee
            // check above), so THIS is what we must guard on — not title.
            // A null sku makes isFee wrongly false, so mark the WHOLE group
            // "incomplete" and skip any orphan verdict for it this pass.
            // recoverCart() gets called again soon (next add/remove, next
            // page load) once sku has resolved.
            if (!line.sku) {
                groups[gid].incomplete = true;
                console.log('[cart-remove-check] line has null sku (still propagating) — marking group', gid, 'incomplete:', { key: line.key, title: line.title });
            }
            (isFee ? groups[gid].fees : groups[gid].products).push(line);
        });

        console.log('[cart-remove-check] groups built:', Object.keys(groups).map(function (gid) {
            return {
                groupID: gid,
                feesCount: groups[gid].fees.length,
                productsCount: groups[gid].products.length,
                feeKeys: groups[gid].fees.map(function (l) { return l.key; }),
                productKeys: groups[gid].products.map(function (l) { return l.key; })
            };
        }));

        var orphanKeys = [];
        Object.keys(groups).forEach(function (gid) {
            var g = groups[gid];
            if (g.incomplete) {
                console.log('[cart-remove-check] group', gid, '=> SKIPPED (line still propagating: title/sku not ready yet) — no verdict this pass');
                return;
            }
            if (g.fees.length && g.products.length === 0) {
                console.log('[cart-remove-check] group', gid, '=> ORPHAN (fee with no product) — removing fee key(s):', g.fees.map(function (l) { return l.key; }));
                g.fees.forEach(function (l) { orphanKeys.push(l.key); });
            } else if (g.products.length && g.fees.length === 0) {
                console.log('[cart-remove-check] group', gid, '=> ORPHAN (product with no fee) — removing product key(s):', g.products.map(function (l) { return l.key; }));
                g.products.forEach(function (l) { orphanKeys.push(l.key); });
            } else {
                console.log('[cart-remove-check] group', gid, '=> healthy (fees:', g.fees.length, ', products:', g.products.length, ') — left untouched');
            }
        });

        console.log('[cart-remove-check] final orphanKeys:', orphanKeys);
        return orphanKeys;
    }

    /**
     * Remove specific lines from the Shopify cart by line key (cart/update.js
     * accepts the line's `key` as the update key, which is unambiguous even
     * when several lines share the same variant_id with different properties).
     *
     * @param {Array<string>} keys
     * @returns {Promise<Object|null>} the freshly-updated cart, or null if nothing to do
     */
    function removeCartLines(keys) {
        if (!keys || !keys.length) return Promise.resolve(null);
        var updates = {};
        keys.forEach(function (k) { updates[k] = 0; });
        return fetch(window.Shopify.routes.root + 'cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates: updates })
        }).then(function (r) {
            if (!r.ok) throw new Error('cart/update.js failed: ' + r.status);
            return r.json(); // the freshly-updated, clean cart
        });
    }

    /**
     * Fully empty the Shopify cart (fallback when targeted removal fails)
     * and write a valid (empty) cart shape into localStorage.cartItems —
     * NOT removing the key entirely, since cart.storage.js's
     * initCartItemsData() has no fallback for that and would leave cartItems
     * uninitialized, breaking the cart page.
     */
    function clearShopifyCart() {
        return fetch(window.Shopify.routes.root + 'cart/clear.js', { method: 'POST' })
            .then(function (r) { return r.json(); })
            .catch(function () { return null; })
            .then(function (clearedCart) {
                try {
                    ['stringProductList', 'fullStorageProductList'].forEach(function (k) {
                        localStorage.removeItem(k);
                    });
                    var emptyCart = clearedCart || { items: [], item_count: 0, total_price: 0 };
                    emptyCart.updated_at = new Date().getTime();
                    localStorage.setItem('cartItems', JSON.stringify(emptyCart));
                } catch (e) { /* ignore storage errors */ }
            });
    }

    function _delay(ms) {
        return new Promise(function (resolve) { setTimeout(resolve, ms); });
    }

    /**
     * Detect and repair a broken cart (orphan lines from a failed add).
     * Safe to call always — a healthy cart is left completely untouched.
     *
     * On success, also writes the cleaned cart straight into
     * localStorage.cartItems (in the exact shape cart.storage.js expects),
     * so that ANY caller that reloads the page afterward (the cart page does)
     * never races against — and potentially cancels mid-flight — some other,
     * slower, independent self-correction flow that would otherwise also try
     * to fix localStorage.
     *
     * @param {number} [attempt] - internal, do not pass
     * @returns {Promise<{broken: boolean, removed?: number, cleared?: boolean, error?: boolean}>}
     */
    function recoverCart(attempt) {
        attempt = attempt || 1;
        console.log('[cart-remove-check] recoverCart() invoked, attempt', attempt);
        console.trace('[cart-remove-check] recoverCart() call stack');
        return fetch(window.Shopify.routes.root + 'cart.js')
            .then(function (r) { return r.json(); })
            .then(function (cart) {
                if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) {
                    return { broken: false };
                }
                var orphanKeys = findOrphanCartLines(cart);
                if (orphanKeys.length === 0) return { broken: false };

                console.warn('[CS-316] Broken cart detected — removing', orphanKeys.length, 'orphan line(s)');
                return removeCartLines(orphanKeys)
                    .then(function (updatedCart) {
                        try {
                            if (updatedCart) {
                                updatedCart.updated_at = new Date().getTime();
                                localStorage.setItem('cartItems', JSON.stringify(updatedCart));
                            }
                        } catch (e) { /* ignore storage errors */ }
                        return { broken: true, removed: orphanKeys.length };
                    })
                    .catch(function (err) {
                        if (attempt < 2) {
                            console.warn('[CS-316] Failed to remove orphan lines (likely a transient race with another concurrent cart/update.js call) — retrying once:', err);
                            return _delay(600).then(function () { return recoverCart(attempt + 1); });
                        }
                        // Still not resolved after one retry — do NOT fall back
                        // to clearShopifyCart(): wiping the whole cart on a
                        // persistent-but-transient failure destroys healthy,
                        // unrelated groups too. Just back off; recoverCart()
                        // will get another shot on the next add/remove/page load.
                        console.error('[CS-316] Failed to remove orphan lines after retry (will retry again on next recoverCart() call, not clearing cart):', err);
                        return { broken: false, error: true };
                    });
            })
            .catch(function (err) {
                console.error('[CS-316] recoverCart() failed:', err);
                return { broken: false, error: true };
            });
    }

    window.__cartRecovery = {
        recoverCart: recoverCart,
        findOrphanCartLines: findOrphanCartLines,
        removeCartLines: removeCartLines,
        clearShopifyCart: clearShopifyCart
    };

    // CS-316: kick off the check IMMEDIATELY when this script executes —
    // NOT on DOMContentLoaded. This file is loaded with `defer`, same as
    // cart.storage.js / cart.core.js right after it; deferred scripts run
    // in document order but all BEFORE DOMContentLoaded fires. Waiting for
    // DOMContentLoaded here meant Vue had already mounted and rendered a
    // broken/orphan line straight from stale localStorage by the time this
    // ran — a visible flicker before the eventual reload(). Starting the
    // (async, non-blocking) fetch chain right now, and exposing the promise,
    // lets cart.core.js's created() hook `await` it BEFORE rendering
    // anything, so the broken state is never shown at all.
    window.__cartRecoveryPromise = recoverCart().then(function (result) {
        if (result && result.broken) {
            console.warn('[CS-316] Recovered broken cart:', result);
        }
        return result;
    });
})();
